import { useState, useEffect } from "react";
import { orderService } from "services";

const IN_PROGRESS_STATUSES = ["pending", "confirmed", "preparing"];
const READY_STATUSES = ["ready"];

/**
 * Lightweight hook for the navbar orders button.
 * Only subscribes when userId is present.
 * Returns badge colour and count for active orders only —
 * completed and cancelled orders are excluded.
 *
 * Badge logic:
 *   - green  → at least one order is "ready" (needs pickup/collection)
 *   - blue   → orders in progress (pending/confirmed/preparing) but nothing ready yet
 *   - null   → no active orders, no badge
 */
export const useActiveOrders = (userId) => {
  const [activeOrders, setActiveOrders] = useState([]);

  useEffect(() => {
    if (!userId) {
      setActiveOrders([]);
      return;
    }

    const unsubscribe = orderService.subscribeToUserOrders(userId, (orders) => {
      const active = orders.filter(
        (o) => !["completed", "cancelled"].includes(o.status),
      );
      setActiveOrders(active);
    });

    return unsubscribe;
  }, [userId]);

  const readyCount = activeOrders.filter((o) =>
    READY_STATUSES.includes(o.status),
  ).length;

  const inProgressCount = activeOrders.filter((o) =>
    IN_PROGRESS_STATUSES.includes(o.status),
  ).length;

  const totalActive = activeOrders.length;

  // Colour priority: green (ready) > blue (in progress) > none
  const badgeColor = readyCount > 0 ? "success" : inProgressCount > 0 ? "info" : null;

  // Badge shows ready count if any, otherwise in-progress count
  const badgeCount = readyCount > 0 ? readyCount : inProgressCount > 0 ? inProgressCount : 0;

  const tooltip =
    readyCount > 0
      ? `${readyCount} order${readyCount !== 1 ? "s" : ""} ready for collection`
      : inProgressCount > 0
        ? `${inProgressCount} order${inProgressCount !== 1 ? "s" : ""} in progress`
        : "View your orders";

  return { badgeColor, badgeCount, totalActive, readyCount, inProgressCount, tooltip };
};
